import { z } from "zod";

// Authentication Schemas
export const signInSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters"),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters"),
});

export const signUpSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters"),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters"),
  anglerName: z
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
});

// Catch Submission Schema
export const catchSchema = z.object({
  fish_species: z
    .string()
    .trim()
    .min(1, "Fish species is required")
    .max(100, "Species name must be less than 100 characters")
    .regex(/^[a-zA-Z\s-]+$/, "Species name can only contain letters, spaces, and hyphens"),
  date_caught: z
    .string()
    .min(1, "Date is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  weight_lbs: z
    .string()
    .min(1, "Weight is required")
    .refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0 && parseFloat(val) <= 10000, {
      message: "Weight must be a positive number less than 10,000 lbs",
    }),
  length_inches: z
    .string()
    .optional()
    .refine((val) => !val || (!isNaN(parseFloat(val)) && parseFloat(val) > 0 && parseFloat(val) <= 500), {
      message: "Length must be a positive number less than 500 inches",
    }),
  girth_inches: z
    .string()
    .optional()
    .refine((val) => !val || (!isNaN(parseFloat(val)) && parseFloat(val) > 0 && parseFloat(val) <= 500), {
      message: "Girth must be a positive number less than 500 inches",
    }),
  general_location: z
    .string()
    .trim()
    .min(1, "Location is required")
    .max(200, "Location must be less than 200 characters"),
  boat_name: z
    .string()
    .trim()
    .max(100, "Boat name must be less than 100 characters")
    .optional(),
  captain_name: z
    .string()
    .trim()
    .max(100, "Captain name must be less than 100 characters")
    .optional(),
  line_test: z
    .string()
    .trim()
    .max(50, "Line test must be less than 50 characters")
    .optional(),
  angler_notes: z
    .string()
    .trim()
    .max(5000, "Notes must be less than 5000 characters")
    .optional(),
});

// Profile Schema
export const profileSchema = z.object({
  angler_name: z
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  nickname: z
    .string()
    .trim()
    .max(50, "Nickname must be less than 50 characters")
    .optional(),
  street_address: z
    .string()
    .trim()
    .max(200, "Address must be less than 200 characters")
    .optional(),
  city: z
    .string()
    .trim()
    .max(100, "City must be less than 100 characters")
    .optional(),
  state: z
    .string()
    .trim()
    .max(2, "State must be 2 characters")
    .regex(/^[A-Z]{0,2}$/, "State must be a valid 2-letter code (e.g., CA, TX)")
    .optional(),
  zip: z
    .string()
    .trim()
    .max(10, "ZIP must be less than 10 characters")
    .regex(/^[0-9]{0,5}(-[0-9]{4})?$/, "ZIP must be a valid format (e.g., 12345 or 12345-6789)")
    .optional(),
  phone: z
    .string()
    .trim()
    .max(20, "Phone must be less than 20 characters")
    .regex(/^[\d\s\-\(\)\+]*$/, "Phone can only contain numbers, spaces, and basic formatting characters")
    .optional(),
});

// Admin User Creation Schema
export const adminCreateUserSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters"),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters"),
  angler_name: z
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
});

// Type exports for TypeScript
export type SignInFormData = z.infer<typeof signInSchema>;
export type SignUpFormData = z.infer<typeof signUpSchema>;
export type CatchFormData = z.infer<typeof catchSchema>;
export type ProfileFormData = z.infer<typeof profileSchema>;
export type AdminCreateUserFormData = z.infer<typeof adminCreateUserSchema>;
